import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ReviewContent } from 'pages/Ai/types';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as ResetIcon } from 'assets/icons/reset.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash2.svg';
import Button from 'components/common/Button/Button';
import ModalDialog from 'components/common/ModalDialog/ModalDialog';
import ToggleInput from 'components/common/ToggleInput/ToggleInput';
import requestAPI from 'utils/apiHandler';

import style from './ExportReview.module.css';

interface Props {
  data: ReviewContent[];
  documentId: string;
}
const ExportReview = ({ data, documentId }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [printAll, setPrintAll] = useState(true);
  const [removeFiles, setRemveFiles] = useState<string[]>([]);
  const [showRemoveFile, setShowRemoveFile] = useState<string>('');
  const [showRestoreFile, setShowRestoreFile] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const downloadZip = () => {
    setLoading(true);
    fetch(`${window.ENV.REACT_APP_BACKEND_URL}tiptap/pdf`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${localStorage.getItem('tokenNewApi')}`,
        'content-type': 'application/json',
      }),
      body: JSON.stringify({
        documentId,
        exclude: removeFiles,
      }),
    })
      .then(res => res.blob())
      .then(blob => {
        const file = window.URL.createObjectURL(blob);
        window.location.assign(file);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setOpen(true);
        }}
        label={t('common:download')}
        startIcon={<DownloadIcon />}
      />
      <ModalDialog
        className="modal-sm"
        open={open}
        onClose={() => {
          setOpen(false);
          setPrintAll(false);
          setRemveFiles([]);
        }}
        title={t('aireview:Download_files')}
        actions={[
          <Button
            isLoading={loading}
            onClick={() => downloadZip()}
            key="download"
            fullWidth={true}
            label={t('common:download')}
          />,
        ]}
      >
        <br />
        <ToggleInput
          label={t('aireview:Download_all_files')}
          checked={printAll}
          value="export_all"
          size="small"
          onChange={val => {
            setPrintAll(val === 'export_all');
          }}
        />
        <div className="devider" />

        <br />

        <div>
          {data.map(obj => (
            <div
              key={obj.name}
              className={`${printAll && style.disabled} ${style.listItem} ${
                showRemoveFile === obj.name && style.showDeleteMessage
              }
${showRestoreFile === obj.name && style.showRestoreFileMessage}

${removeFiles.indexOf(obj.name) > -1 && style.removed}
`}
            >
              <div>
                {obj.name}{' '}
                {removeFiles.indexOf(obj.name) === -1 && (
                  <span onClick={() => setShowRemoveFile(obj.name)}>
                    <TrashIcon />
                  </span>
                )}
                {removeFiles.indexOf(obj.name) > -1 && (
                  <span onClick={() => setShowRestoreFile(obj.name)}>
                    <ResetIcon width={32} height={32} />
                  </span>
                )}
              </div>
              {
                // Restore message window
              }
              <div className={`${style.message} ${style.restoreMessage}`}>
                <p>
                  {t('aireview:include_export_confirmation', { filename: obj.name })}
                  <br />
                  <strong>
                    <small>{t('aireview:include_export_note')}</small>
                  </strong>
                </p>

                <div className={style.buttons}>
                  <Button
                    onClick={() => {
                      setRemveFiles(val => val.filter(item => item !== showRestoreFile));
                      setShowRestoreFile('');
                    }}
                    label={t('aireview:confirm_inclusion')}
                    size="small"
                  />
                  <Button
                    variant="alt"
                    label={t('aireview:cancel')}
                    onClick={() => setShowRestoreFile('')}
                    size="small"
                  />
                </div>
              </div>
              {
                // End Restore message window
              }
              {
                // Delete message window
              }

              <div className={`${style.message} ${style.deleteMessage} `}>
                <p>
                  {t('aireview:exclude_export_confirmation', { filename: obj.name })}
                  <br />{' '}
                  <strong>
                    <small>{t('aireview:exclude_export_note')}</small>
                  </strong>
                </p>

                <div className={style.buttons}>
                  <Button
                    onClick={() => {
                      setRemveFiles(val => [...val, showRemoveFile]);
                      setShowRemoveFile('');
                    }}
                    variant="decline"
                    label={t('aireview:confirm_removal')}
                    size="small"
                  />
                  <Button onClick={() => setShowRemoveFile('')} label={t('aireview:cancel')} size="small" />
                </div>
              </div>
              {
                //End Delete message window
              }
            </div>
          ))}
        </div>
      </ModalDialog>
    </>
  );
};

export default ExportReview;
